import { Menu, Dialog } from '../FarChat/ChatFeedbackOverlays';
/**
 * SessionsRail — left rail for the homepage multi-session chat.
 *
 * Renders the user's chat sessions grouped by recency
 * (Today / Yesterday / Earlier) and a "+ New chat" button. Each row has
 * an overflow menu with Rename and Delete actions; both are wrapped in
 * MUI dialogs for confirmation / input.
 *
 * The rail is purely presentational — it does NOT fetch. The parent
 * (`HomeChatPanel`) owns server state and re-passes `sessions` after
 * mutations.
 *
 * See:
 *   - issues/20260429-homepage-multi-chat_spec.md §4 (UX/UI)
 *   - issues/20260429-homepage-multi-chat_implplan.md §3.2
 */
import React, { useCallback, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import IconButton from '@mui/material/IconButton';

import MenuItem from '@mui/material/MenuItem';

import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddIcon from '@mui/icons-material/Add';
import { alpha, Theme } from '@mui/material/styles';

import type { ChatSession } from './types';

export type SessionsRailProps = {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSelect: (sessionId: string) => void;
  onNewChat: () => void;
  onRename: (sessionId: string, newTitle: string) => void;
  onDelete: (sessionId: string) => void;
  onOpenChatExamples?: () => void;
  loading?: boolean;
  /** A workflow-only shell displays server-filtered sessions and their management capability. */
  constrained?: boolean;
};

export type RecencyGroups = {
  today: ChatSession[];
  yesterday: ChatSession[];
  earlier: ChatSession[];
};

const TITLE_MAX = 120;

/** The compact, single activity signal used for every working chat row. */
export const SESSION_ACTIVITY_DOT_SX = {
  width: 7,
  height: 7,
  borderRadius: '50%',
  bgcolor: 'success.main',
  '--home-chat-pulse-start': (theme: Theme) => alpha(theme.palette.success.main, 0.35),
  '--home-chat-pulse-end': (theme: Theme) => alpha(theme.palette.success.main, 0),
  boxShadow: '0 0 0 0 var(--home-chat-pulse-start)',
  animation: 'home-chat-activity-pulse 1.8s ease-out infinite',
  '@keyframes home-chat-activity-pulse': {
    '0%': {
      boxShadow: '0 0 0 0 var(--home-chat-pulse-start)',
    },
    '70%': {
      boxShadow: '0 0 0 5px var(--home-chat-pulse-end)',
    },
    '100%': {
      boxShadow: '0 0 0 0 var(--home-chat-pulse-end)',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    animation: 'none',
  },
} as const;

/**
 * Pure helper: bucket sessions by `last_activity_at` calendar date relative
 * to `now`. Boundary semantics:
 *   - "today"     = same calendar date as `now`
 *   - "yesterday" = previous calendar date (now - 1 day)
 *   - "earlier"   = anything older
 *
 * Sessions with unparseable timestamps fall into "earlier" so we never drop
 * rows. Order within each bucket preserves the input order — callers should
 * sort beforehand (server returns DESC by `last_activity_at`).
 */
export function groupByRecency(
  sessions: ChatSession[],
  now: Date
): RecencyGroups {
  const out: RecencyGroups = { today: [], yesterday: [], earlier: [] };
  const todayKey = ymd(now);
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayKey = ymd(yesterday);

  for (const s of sessions) {
    const ts = s.last_activity_at ? new Date(s.last_activity_at) : null;
    if (!ts || isNaN(ts.getTime())) {
      out.earlier.push(s);
      continue;
    }
    const k = ymd(ts);
    if (k === todayKey) out.today.push(s);
    else if (k === yesterdayKey) out.yesterday.push(s);
    else out.earlier.push(s);
  }
  return out;
}

function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function relativeTime(iso: string | null | undefined, now: Date): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const diffMs = now.getTime() - d.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString();
}

/**
 * Single rail row, memoized so a poll / background refresh that returns the
 * SAME data for this session is a no-op render (no DOM churn, no flash). It
 * re-renders ONLY when its own props change — i.e. when this session's
 * title, time, or server-owned activity actually changes, when selected state flips,
 * or when `now` advances. All handler props are stable (`useCallback` in the
 * parent), and `onOpenMenu`/`onSelect` take the id so the parent doesn't have
 * to allocate a per-row closure that would defeat the memo.
 */
type SessionRowProps = {
  session: ChatSession;
  selected: boolean;
  now: Date;
  onSelect: (sessionId: string) => void;
  onOpenMenu: (e: React.MouseEvent<HTMLElement>, sessionId: string) => void;
  canManage?: boolean;
};

const SessionRow: React.FC<SessionRowProps> = React.memo(
  ({ session, selected, now, onSelect, onOpenMenu, canManage = true }) => {
    // `title: null` rows render as "New Chat" — matching the "+ New chat"
    // button label and the client placeholder. This covers (a) the
    // client-only `'pending'` placeholder inserted on "+ New chat" click
    // (lazy-create — no server row yet), and (b) any server rows whose
    // first turn hasn't auto-titled yet.
    const primary = session.title || 'New Chat';
    const secondary = relativeTime(session.last_activity_at, now);
    const isWorking = Boolean(session.activity);
    const isReady = !isWorking
      && session.completion?.state === 'completed'
      && session.completion.unseen;
    return (
      <ListItem
        disablePadding
        secondaryAction={canManage ? (
          <IconButton
            edge="end"
            size="small"
            aria-label={`More actions for ${primary}`}
            onClick={(e) => onOpenMenu(e, session.session_id)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ) : undefined}
      >
        <ListItemButton
          selected={selected}
          onClick={() => onSelect(session.session_id)}
        >
          <ListItemText
            primary={primary}
            secondary={
              isWorking ? (
                <Box
                  component="span"
                  role="status"
                  aria-label={`Working: ${primary}`}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    color: 'success.dark',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    lineHeight: 1.35,
                  }}
                >
                  <Box
                    component="span"
                    data-testid={`session-activity-dot-${session.session_id}`}
                    aria-hidden="true"
                    sx={SESSION_ACTIVITY_DOT_SX}
                  />
                  Working
                </Box>
              ) : isReady ? (
                <Box
                  component="span"
                  role="status"
                  aria-label={`Ready: ${primary}`}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', color: 'success.dark',
                    fontSize: '0.75rem', fontWeight: 600, lineHeight: 1.35,
                  }}
                >
                  Ready
                </Box>
              ) : secondary
            }
            primaryTypographyProps={{
              noWrap: true,
              fontWeight: selected ? 600 : 400,
            }}
            secondaryTypographyProps={isWorking || isReady ? undefined : { variant: 'caption' }}
          />
        </ListItemButton>
      </ListItem>
    );
  }
);
SessionRow.displayName = 'SessionRow';

const SessionsRail: React.FC<SessionsRailProps> = ({
  sessions,
  activeSessionId,
  onSelect,
  onNewChat,
  onRename,
  onDelete,
  onOpenChatExamples,
  loading = false,
  constrained = false,
}) => {
  // Recompute groups when the server-owned session list changes. Capture one
  // timestamp inside that calculation so every row uses the same boundary.
  const groupedSessions = useMemo(() => {
    const now = new Date();
    return { now, groups: groupByRecency(sessions, now) };
  }, [sessions]);
  const { now, groups } = groupedSessions;

  // Overflow-menu state (anchor + which session it's for)
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);

  // Rename dialog
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renameTargetId, setRenameTargetId] = useState<string | null>(null);

  // Delete confirm dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);

  const trimmed = renameValue.trim();
  const renameInvalid = trimmed.length === 0 || trimmed.length > TITLE_MAX;

  // Stable handler identities so the memoized `SessionRow`s don't re-render
  // just because the parent re-rendered. `openMenu`/`handleSelect` take the
  // session id as an argument (the row passes it), keeping a single shared
  // callback rather than one closure per row.
  const openMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>, sessionId: string) => {
      e.stopPropagation();
      setMenuAnchor(e.currentTarget);
      setMenuSessionId(sessionId);
    },
    []
  );

  const handleSelect = useCallback(
    (sessionId: string) => {
      onSelect(sessionId);
    },
    [onSelect]
  );

  function closeMenu() {
    setMenuAnchor(null);
    setMenuSessionId(null);
  }

  function canManageSession(sessionId: string | null): boolean {
    const session = sessions.find((item) => item.session_id === sessionId);
    return Boolean(session && (!constrained || session.can_manage === true));
  }

  function startRename() {
    const id = menuSessionId;
    if (!id || !canManageSession(id)) {
      closeMenu();
      return;
    }
    const target = sessions.find((s) => s.session_id === id);
    setRenameTargetId(id);
    setRenameValue(target?.title || '');
    setRenameOpen(true);
    closeMenu();
  }

  function startDelete() {
    const id = menuSessionId;
    if (!id || !canManageSession(id)) {
      closeMenu();
      return;
    }
    setDeleteTargetId(id);
    setDeleteOpen(true);
    closeMenu();
  }

  function confirmRename() {
    if (renameInvalid || !renameTargetId || !canManageSession(renameTargetId)) return;
    onRename(renameTargetId, trimmed);
    setRenameOpen(false);
    setRenameTargetId(null);
    setRenameValue('');
  }

  function confirmDelete() {
    if (!deleteTargetId || !canManageSession(deleteTargetId)) return;
    onDelete(deleteTargetId);
    setDeleteOpen(false);
    setDeleteTargetId(null);
  }

  // Stable key = `session_id` (NEVER index) so rows reconcile in place across
  // refetches instead of re-mounting/flashing.
  function renderRow(s: ChatSession) {
    return (
      <SessionRow
        key={s.session_id}
        session={s}
        selected={s.session_id === activeSessionId}
        now={now}
        onSelect={handleSelect}
        onOpenMenu={openMenu}
        canManage={!constrained || s.can_manage === true}
      />
    );
  }

  const isEmpty = !loading && sessions.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {!constrained && <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Button onClick={onNewChat} variant="outlined" fullWidth startIcon={<AddIcon />}>New chat</Button>
        {onOpenChatExamples && <Button fullWidth onClick={onOpenChatExamples} sx={{ mt: 1 }}>Chat examples</Button>}
      </Box>}
      {constrained && <Box sx={{ px: 1.5, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2">Your workflow chats</Typography>
        <Typography variant="caption" color="text.secondary">Open an authorized workflow chat.</Typography>
      </Box>}

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 1.5 }} data-testid="sessions-rail-loading">
            <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" height={40} />
          </Box>
        ) : isEmpty ? (
          <Box sx={{ p: 2 }} data-testid="sessions-rail-empty">
            <Typography variant="body2" color="text.secondary">
              {constrained ? 'No saved workflow chats yet. Start one above.' : 'No chats yet. Start one with "+ New chat".'}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {groups.today.length > 0 && (
              <>
                <ListSubheader disableSticky>Today</ListSubheader>
                {groups.today.map(renderRow)}
              </>
            )}
            {groups.yesterday.length > 0 && (
              <>
                <ListSubheader disableSticky>Yesterday</ListSubheader>
                {groups.yesterday.map(renderRow)}
              </>
            )}
            {groups.earlier.length > 0 && (
              <>
                <ListSubheader disableSticky>Earlier</ListSubheader>
                {groups.earlier.map(renderRow)}
              </>
            )}
          </List>
        )}
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        // The HomeChatLauncher Dialog wrapping this rail is bumped to
        // zIndex 2200 to clear the app navbar (zIndex 2000). MUI's Menu is
        // a Popover that defaults to theme.zIndex.modal (1300), so without
        // this override it renders behind the parent Dialog and looks like
        // the click "did nothing". Sit above 2200 — mirrors the pattern in
        // FarChat/CitationMarker.tsx for tooltips inside that popup.
        sx={{ zIndex: (theme) => Math.max(theme.zIndex.modal, 2200) + 50 }}
      >
        <MenuItem onClick={startRename}>Rename</MenuItem>
        <MenuItem onClick={startDelete}>Delete</MenuItem>
      </Menu>

      <Dialog
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        fullWidth
        maxWidth="sm"
        // Same reason as the Menu above — must clear the parent
        // HomeChatLauncher Dialog at zIndex 2200.
        sx={{ zIndex: (theme) => Math.max(theme.zIndex.modal, 2200) + 50 }}
      >
        <DialogTitle>Rename chat</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Title"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            inputProps={{ 'aria-label': 'Chat title' }}
            helperText={`${trimmed.length}/${TITLE_MAX}`}
            error={renameInvalid && renameValue.length > 0}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmRename}
            variant="contained"
            disabled={renameInvalid}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        // Same reason as the Menu above — must clear the parent
        // HomeChatLauncher Dialog at zIndex 2200.
        sx={{ zIndex: (theme) => Math.max(theme.zIndex.modal, 2200) + 50 }}
      >
        <DialogTitle>Delete chat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete this chat? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default SessionsRail;
